import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:  { bg:'#FFF3DC', color:'#8B5E0A', label:'Pending Approval' },
  approved: { bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  rejected: { bg:'#FDEAEA', color:'#8F2E24', label:'Rejected' },
};

const ADJUSTMENT_TYPES = [
  { value:'approved_shortage', label:'Approved Shortage' },
  { value:'approved_surplus', label:'Approved Surplus' },
  { value:'damage', label:'Damage' },
  { value:'write_off', label:'Write-Off' },
  { value:'count_correction', label:'Count Correction' },
  { value:'transaction_correction', label:'Transaction Correction' },
];

export default function InventoryAdjustments() {
  const { user } = useAuth();
  const canWrite = ['super_admin','admin','inventory_officer'].includes(user?.role);
  const canApprove = ['super_admin','admin'].includes(user?.role);

  const [adjustments, setAdjustments] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/inventory-control/adjustments'),
      get('/inventory-control/skus'),
      get('/materials')
    ]).then(([a, s, m]) => {
      setAdjustments(a.data || []);
      setSkus(s.data || []);
      setMaterials(m.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = adjustments.filter(a => {
    const matchStatus = !statusFilter || a.status === statusFilter;
    const matchSearch = !search ||
      a.adjustment_number?.toLowerCase().includes(search.toLowerCase()) ||
      (a.item_description || a.sku_description)?.toLowerCase().includes(search.toLowerCase()) ||
      a.reason?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const inventoryItems = [
    ...materials
      .filter(
        material =>
          material.is_active !== false
      )
      .map(material => ({
        key:
          'material:' + material.id,
        item_type: 'material',
        item_id: material.id,
        item_code:
          material.material_code,
        description: material.name,
        unit_of_measure:
          material.unit_of_measure,
        current_balance:
          material.current_stock || 0,
        reserved_quantity: 0,
        available_balance:
          material.current_stock || 0
      })),
    ...skus
      .filter(
        sku =>
          sku.is_active !== false
      )
      .map(sku => ({
        key: 'sku:' + sku.id,
        item_type: 'sku',
        item_id: sku.id,
        item_code: sku.sku,
        description:
          sku.description,
        unit_of_measure:
          sku.unit_of_measure,
        current_balance:
          sku.current_balance || 0,
        reserved_quantity:
          sku.reserved_quantity || 0,
        available_balance:
          sku.available_balance || 0,
        material_name:
          sku.material_name
      }))
  ];

  const selectedItem =
    inventoryItems.find(
      item =>
        item.key ===
        modal?.item_key
    );

  const openCreate = () => {
    setError('');
    setModal({
      item_key:'', adjustment_type:'count_correction',
      adjustment_quantity:'', reason:'', evidence_url:''
    });
  };

  const save = async () => {
    setError('');

    if (
      !modal.item_key ||
      !modal.adjustment_type ||
      modal.adjustment_quantity === '' ||
      !String(modal.reason || '').trim()
    ) {
      setError(
        'Inventory item, type, quantity ' +
        'and reason are required'
      );
      return;
    }

    const item = inventoryItems.find(
      record =>
        record.key === modal.item_key
    );

    if (!item) {
      setError(
        'Select a valid inventory item'
      );
      return;
    }

    setSaving(true);

    try {
      await post(
        '/inventory-control/adjustments',
        {
          item_type: item.item_type,
          item_id: item.item_id,
          adjustment_type:
            modal.adjustment_type,
          adjustment_quantity:
            modal.adjustment_quantity,
          reason:
            String(modal.reason).trim(),
          evidence_url:
            modal.evidence_url || null
        }
      );

      setModal(null);
      load();
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this adjustment? This will permanently update stock balance.')) return;
    try {
      await put('/inventory-control/adjustments/'+id+'/approve', {});
      load();
    } catch(e) { alert(e.message); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await put('/inventory-control/adjustments/'+id+'/reject', { rejection_reason: reason });
      load();
    } catch(e) { alert(e.message); }
  };

  const counts = {
    pending: adjustments.filter(a=>a.status==='pending').length,
    approved: adjustments.filter(a=>a.status==='approved').length,
    rejected: adjustments.filter(a=>a.status==='rejected').length,
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Inventory Adjustments</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Request and approve corrections to inventory balances
          </p>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={openCreate}>{user?.role==='super_admin'?'+ Apply Adjustment':'+ Request Adjustment'}</button>
        )}
      </div>

      {/* Important notice */}
      <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
        padding:'10px 16px',marginBottom:20,fontSize:12,color:'#8F2E24'}}>
        ⚠ Stock balances can <strong>never be edited directly</strong>. All corrections must go
        through an adjustment request and require management approval before posting to the ledger.
      </div>

      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
        {Object.entries(counts).map(([status, count]) => {
          const sc = STATUS_COLORS[status];
          return (
            <div key={status} onClick={()=>setStatusFilter(statusFilter===status?'':status)}
              style={{background:statusFilter===status?sc.bg:'#fff',
                border:`1px solid ${sc.color}44`,borderRadius:10,padding:'12px 16px',cursor:'pointer'}}>
              <div style={{fontSize:9,fontWeight:700,letterSpacing:'.1em',
                textTransform:'uppercase',color:sc.color,marginBottom:4}}>{sc.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',
                fontWeight:700,color:sc.color}}>{count}</div>
            </div>
          );
        })}
      </div>

      {counts.pending > 0 && canApprove && (
        <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
          padding:'10px 16px',marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
          ⏳ <strong>{counts.pending}</strong> adjustment{counts.pending!==1?'s':''} awaiting your approval
        </div>
      )}

      {/* Filters */}
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search adjustment number, item or reason..."
          value={search} onChange={e=>setSearch(e.target.value)} style={{flex:1}} />
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
          className="form-control" style={{width:'auto'}}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_COLORS).map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"/> : (
          <table>
            <thead><tr style={{background:'#3B2A21'}}>
              <th style={{color:'#F2EFE7'}}>Reference</th>
              <th style={{color:'#F2EFE7'}}>Inventory Item</th>
              <th style={{color:'#F2EFE7'}}>Type</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Current Qty</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Adjustment</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Resulting Qty</th>
              <th style={{color:'#F2EFE7'}}>Reason</th>
              <th style={{color:'#F2EFE7'}}>Requested By</th>
              <th style={{color:'#F2EFE7'}}>Status</th>
              {(canApprove||canWrite) && <th></th>}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="10">
                  <div className="empty-state">
                    <h3>No adjustments found</h3>
                    {canWrite && (
                      <button className="btn btn-primary" onClick={openCreate}>
                        Request First Adjustment
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : filtered.map(a => {
                const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
                const adj = parseFloat(a.adjustment_quantity);
                return (
                  <tr key={a.id}>
                    <td style={{fontWeight:700,fontSize:11,fontFamily:'monospace',color:'#3B2A21'}}>
                      {a.adjustment_number}
                    </td>
                    <td>
                      <div style={{
                        fontSize:12,
                        fontWeight:600
                      }}>
                        {a.item_description ||
                          a.sku_description}
                      </div>
                      <div style={{
                        fontSize:10,
                        color:'#8A8A8A'
                      }}>
                        {a.item_code || ''}
                      </div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{a.unit_of_measure}</div>
                    </td>
                    <td style={{fontSize:11,color:'#555'}}>
                      {ADJUSTMENT_TYPES.find(t=>t.value===a.adjustment_type)?.label||a.adjustment_type}
                    </td>
                    <td className="amt">{Number(a.current_quantity||0).toLocaleString()}</td>
                    <td className="amt" style={{
                      fontWeight:700,
                      color: adj > 0 ? '#2E6B3A' : adj < 0 ? '#8F2E24' : '#8A8A8A'
                    }}>
                      {adj > 0 ? '+' : ''}{Number(adj).toLocaleString()}
                    </td>
                    <td className="amt" style={{fontWeight:700,color:'#3B2A21'}}>
                      {Number(a.resulting_quantity||0).toLocaleString()}
                    </td>
                    <td style={{fontSize:12,maxWidth:200,color:'#555'}}>{a.reason}</td>
                    <td style={{fontSize:11,color:'#8A8A8A'}}>{a.requested_by_name}</td>
                    <td>
                      <span style={{...sc,fontSize:9,fontWeight:700,padding:'2px 8px',
                        borderRadius:20,textTransform:'uppercase'}}>
                        {sc.label}
                      </span>
                    </td>
                    {(canApprove||canWrite) && (
                      <td>
                        {canApprove && a.status === 'pending' && (
                          <div style={{display:'flex',gap:4}}>
                            <button className="btn btn-primary btn-sm"
                              style={{background:'#2E6B3A',border:'none',fontSize:11}}
                              onClick={()=>approve(a.id)}>Approve</button>
                            <button className="btn btn-outline btn-sm"
                              style={{color:'#8F2E24',borderColor:'#8F2E24',fontSize:11}}
                              onClick={()=>reject(a.id)}>Reject</button>
                          </div>
                        )}
                        {a.status === 'approved' && (
                          <div style={{fontSize:10,color:'#2E6B3A'}}>
                            By {a.approved_by_name}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-hdr">
              <span className="modal-title">{user?.role==='super_admin'?'Apply Inventory Adjustment':'Request Inventory Adjustment'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
                padding:12,marginBottom:16,fontSize:12,color:'#8B5E0A'}}>
                {user?.role==='super_admin'?'Saving records your approval and posts this correction to the stock ledger.':'This request will be sent to management for approval. Stock will only change after approval. You cannot approve your own request.'}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Inventory Item *
                </label>
                <select
                  className="form-control"
                  value={modal.item_key||''}
                  onChange={e=>setModal({
                    ...modal,
                    item_key:e.target.value
                  })}
                >
                  <option value="">
                    Select parent material or child SKU...
                  </option>

                  <optgroup label="Parent Materials">
                    {inventoryItems
                      .filter(
                        item =>
                          item.item_type ===
                          'material'
                      )
                      .map(item=>(
                        <option
                          key={item.key}
                          value={item.key}
                        >
                          {item.item_code} — {item.description}
                          {' '}(Current: {Number(
                            item.current_balance || 0
                          ).toLocaleString()} {item.unit_of_measure})
                        </option>
                      ))}
                  </optgroup>

                  <optgroup label="Child SKUs">
                    {inventoryItems
                      .filter(
                        item =>
                          item.item_type ===
                          'sku'
                      )
                      .map(item=>(
                        <option
                          key={item.key}
                          value={item.key}
                        >
                          {item.item_code} — {item.description}
                          {' '}({item.material_name || 'Parent not named'})
                          {' '}(Current: {Number(
                            item.current_balance || 0
                          ).toLocaleString()} {item.unit_of_measure})
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>

              {/* Show current balance */}
              {selectedItem && (
                <div style={{background:'#F9F8F5',borderRadius:8,padding:'10px 14px',
                  marginBottom:14,fontSize:12}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                    {[
                      ['Current Balance', Number(selectedItem.current_balance||0).toLocaleString()],
                      ['Reserved', Number(selectedItem.reserved_quantity||0).toLocaleString()],
                      ['Available', Number(selectedItem.available_balance||0).toLocaleString()],
                    ].map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',marginBottom:2}}>{l}</div>
                        <div style={{fontSize:14,fontWeight:700,color:'#3B2A21'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Adjustment Type *</label>
                <select className="form-control" value={modal.adjustment_type||'count_correction'}
                  onChange={e=>setModal({...modal,adjustment_type:e.target.value})}>
                  {ADJUSTMENT_TYPES.map(t=>(
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Adjustment Quantity *</label>
                <input type="number" className="form-control"
                  value={modal.adjustment_quantity||''}
                  onChange={e=>setModal({...modal,adjustment_quantity:e.target.value})}
                  placeholder="Use negative number to reduce stock (e.g. -5), positive to increase (e.g. +3)"
                  step="0.01" />
                <div className="form-hint">
                  Use a negative number to reduce stock, positive to increase.
                  {selectedItem && modal.adjustment_quantity !== '' && (
                    <span style={{marginLeft:4,fontWeight:600,
                      color: parseFloat(selectedItem.current_balance||0) + parseFloat(modal.adjustment_quantity||0) < 0
                        ? '#8F2E24' : '#2E6B3A'}}>
                      Result: {(parseFloat(selectedItem.current_balance||0) + parseFloat(modal.adjustment_quantity||0)).toLocaleString()} {selectedItem.unit_of_measure}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Reason *</label>
                <textarea className="form-control" rows={3}
                  value={modal.reason||''}
                  onChange={e=>setModal({...modal,reason:e.target.value})}
                  placeholder="Explain why this adjustment is needed. Be specific — management needs sufficient detail to approve." />
              </div>

              <div className="form-group">
                <label className="form-label">Evidence / Reference</label>
                <input className="form-control" value={modal.evidence_url||''}
                  onChange={e=>setModal({...modal,evidence_url:e.target.value})}
                  placeholder="Stock count reference, document number etc." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':user?.role==='super_admin'?'Approve & Apply Adjustment':'Submit for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
